import {
    Card,
    Collapse,
    Empty,
    Flex,
    notification,
    Spin,
    Typography,
} from "antd";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {getRequest} from "../../api/httpRequest";
import {IAdmissionStatement} from "./model/admissionModel";
import Result from "antd/lib/result";
import AdmissionStatementItem from "./AdmissionStatementItem";
import AdmissionListContactsBody, {ADMISSION_CONTACT_EMAIL_ADDRESS} from "./AdmissionListContactsBody";
import {userStore} from "../../store/store";
import {FaRegArrowAltCircleDown} from "react-icons/fa";
import {IUserData} from "../../store/IUserData";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";

const {Title} = Typography;

function buildAdmissionMailtoHref(user: IUserData): string {
    const parts = [user.lastName, user.firstName, user.middleName]
        .map((s) => (s ?? "").trim())
        .filter(Boolean);
    const fio = parts.join(" ");
    const subject = `Приемная кампания:  ${fio || "—"}`;
    return `mailto:${ADMISSION_CONTACT_EMAIL_ADDRESS}?subject=${encodeURIComponent(subject)}`;
}

function isPaidStatement(statement: IAdmissionStatement): boolean {
    const basis = (statement.BasisTraining || "").toLowerCase();
    return basis.includes("места по договорам") || basis.includes("контракт");
}

export default function AdmissionList() {

    const [filteredStatements, setFilteredStatements] = useState<IAdmissionStatement[]>([]);
    const [idSs, setIdSs] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [api, contextHolder] = notification.useNotification()
    const [warning, setWarning] = useState<boolean>(false);
    const [showScrollArrow, setShowScrollArrow] = useState(false);
    const mainColumnRef = useRef<HTMLDivElement>(null);
    const {user} = userStore();
    const {xl} = useBreakpoint();
    const isMobile = !xl;

    const admissionMailtoHref = useMemo(
        () => buildAdmissionMailtoHref(user),
        [user.lastName, user.firstName, user.middleName],
    );

    const fetchStatements = () => {
        setLoading(true);
        getRequest("/server/proxy/contracts/StudApplica")
            .then(res => {
                const statementsData = res.data.admissions
                if (Array.isArray(statementsData)) {
                    setFilteredStatements(statementsData.filter((item: IAdmissionStatement) => item.Current));
                    setIdSs(res.data.IdSS);
                } else {
                    setFilteredStatements([]);
                }
            })
            .catch(err => {
                if (err.response.status === 404) {
                    setWarning(true);
                    return
                }
                api.error({
                    message: `Ошибка загрузки данных`,
                    duration: 3
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const handleScroll = useCallback(() => {
        const el = mainColumnRef.current;
        if (!el) return;
        setShowScrollArrow(el.scrollHeight - el.scrollTop - el.clientHeight > 16);
    }, []);

    useEffect(() => {
        fetchStatements();
    }, []);

    useEffect(() => {
        const el = mainColumnRef.current;
        if (!el) return;
        el.addEventListener("scroll", handleScroll, {passive: true});
        handleScroll();
        return () => el.removeEventListener("scroll", handleScroll);
    }, [handleScroll, filteredStatements]);

    const mobileContactsItems = useMemo(
        () => [
            {
                key: "reference",
                label: "Контакты и идентификатор в списках",
                children: (
                    <div className={"admission-list-mobile-contacts-inner"}>
                        <AdmissionListContactsBody
                            user={user}
                            admissionMailtoHref={admissionMailtoHref}
                            idSs={idSs}
                        />
                    </div>
                ),
            },
        ],
        [user, admissionMailtoHref, idSs],
    );

    return (
        <Flex className={"admission-list admission-list-scroll-layout"} vertical align={"stretch"}>
            {contextHolder}
            {warning &&
                <Result
                    status="warning"
                    title={<div>На сайте ведутся технические работы.
                        Личный кабинет недоступен до окончания работ. Для подачи документов воспользуйтесь,
                        пожалуйста, порталом <a href="https://gosuslugi.ru/vuzonline">"Госуслуги"</a></div>}
                />
            }
            {!warning &&
                <>
                    <Flex className={"admission-list-header-row"} align="flex-start">
                        <Title level={3} className={"admission-list-page-title"}>
                            Мои заявления
                        </Title>
                    </Flex>
                    {isMobile && (
                        <Collapse
                            className={"admission-list-mobile-contacts"}
                            bordered={false}
                            expandIconPosition="end"
                            defaultActiveKey={[]}
                            items={mobileContactsItems}
                        />
                    )}
                    <Flex className={"admission-list-body"} gap={16} align={"flex-start"} wrap={false}>
                        <div className={"admission-list-main-column"} ref={mainColumnRef}>
                            {loading ? (
                                <Flex justify="center" align="center" style={{minHeight: "200px"}}>
                                    <Spin size="large" tip="Загрузка заявлений..."/>
                                </Flex>
                            ) : filteredStatements.length > 0 ? (
                                <Flex vertical gap={16}>
                                    {filteredStatements.map((item, index) => (
                                        <AdmissionStatementItem
                                            key={`${item.UID}-${index}`}
                                            statement={item}
                                            showContractColumn={isPaidStatement(item)}
                                        />
                                    ))}
                                </Flex>
                            ) : (
                                <Card className={"admission-empty-card"}>
                                    <Empty description="Заявления не подавались"/>
                                </Card>
                            )}
                            {showScrollArrow && (
                                <div className={"admission-list-scroll-indicator"}>
                                    <FaRegArrowAltCircleDown/>
                                </div>
                            )}
                        </div>
                        {!isMobile && (
                            <aside
                                className={"admission-list-contacts-card"}
                                aria-label={"Справочная информация: идентификатор в конкурсных списках и контакты приёмной комиссии"}
                            >
                                <AdmissionListContactsBody
                                    user={user}
                                    admissionMailtoHref={admissionMailtoHref}
                                    idSs={idSs}
                                />
                            </aside>
                        )}
                    </Flex>
                </>
            }
        </Flex>
    )
}
